// Cross-region contrast computation for the comparison table.
//
// This module is intentionally pure and side-effect free so the highlight
// rules can be unit tested in isolation. It takes the normalized region rows
// (see domain/normalizeApp.js) and returns derived contrast facts: which
// storefronts lead or trail on rating count and rating, which prices are
// unusual, which are unavailable, plus a per-row tag list and a one-line
// insight string.
//
// Honesty note: rating count is a public *proxy* for popularity. Nothing here
// estimates installs, downloads, revenue, or market share. We only rank the
// numbers Apple actually exposes.

import { detectLocalizationGap } from './localizationGap.js';

/**
 * @typedef {import('./normalizeApp.js').RegionRow} RegionRow
 */

/**
 * @typedef {object} RegionContrast
 * @property {string} countryCode
 * @property {boolean} available
 * @property {number} ratingCountShare - userRatingCount / maxRatingCount, in
 *   [0,1]. 0 when there is no positive max. Used for the popularity-proxy bar.
 * @property {number|null} ratingDeviation - averageUserRating minus global mean,
 *   rounded to 2 decimal places. null when rating is unavailable or < 2 rows
 *   have ratings (deviation is meaningless for a single data point).
 * @property {import('./localizationGap.js').LocalizationGap|null} localizationGap
 * @property {string[]} tags - subset of:
 *   'rating-count-leader', 'rating-count-low', 'rating-leader', 'rating-low',
 *   'rating-above-avg', 'rating-below-avg',
 *   'emerging', 'low-visibility', 'price-mismatch', 'localization-gap',
 *   'unavailable'.
 */

/**
 * @typedef {object} ContrastResult
 * @property {Map<string, RegionContrast>} byRegion - keyed by countryCode.
 * @property {number} maxRatingCount - max userRatingCount among available rows.
 * @property {string|null} mostTractionCode - region with highest rating count.
 * @property {string|null} weakestCode - available region with lowest rating count.
 * @property {string} insight - one-line human summary, e.g.
 *   "Most traction: Japan (JP) · Weakest: China mainland (CN)".
 */

const TAGS = Object.freeze({
  RATING_COUNT_LEADER: 'rating-count-leader',
  RATING_COUNT_LOW: 'rating-count-low',
  RATING_LEADER: 'rating-leader',
  RATING_LOW: 'rating-low',
  RATING_ABOVE_AVG: 'rating-above-avg',
  RATING_BELOW_AVG: 'rating-below-avg',
  EMERGING: 'emerging',
  LOW_VISIBILITY: 'low-visibility',
  PRICE_MISMATCH: 'price-mismatch',
  LOCALIZATION_GAP: 'localization-gap',
  UNAVAILABLE: 'unavailable',
});

export { TAGS };

/**
 * Human-readable label for each contrast tag, used by the UI for badge text.
 * @param {string} tag
 * @returns {string}
 */
export function tagLabel(tag) {
  switch (tag) {
    case TAGS.RATING_COUNT_LEADER:
      return 'Most traction';
    case TAGS.RATING_COUNT_LOW:
      return 'Least traction';
    case TAGS.RATING_LEADER:
      return 'Top rated';
    case TAGS.RATING_LOW:
      return 'Lowest rated';
    case TAGS.RATING_ABOVE_AVG:
      return 'Above avg';
    case TAGS.RATING_BELOW_AVG:
      return 'Below avg';
    case TAGS.EMERGING:
      return 'Emerging';
    case TAGS.LOW_VISIBILITY:
      return 'Low visibility';
    case TAGS.PRICE_MISMATCH:
      return 'Price outlier';
    case TAGS.LOCALIZATION_GAP:
      return 'Missing local language';
    case TAGS.UNAVAILABLE:
      return 'Unavailable';
    default:
      return tag;
  }
}

/**
 * Compute cross-region contrast facts for a set of normalized rows.
 *
 * Highlight rules (deterministic, explainable; see docs/PLAN.md):
 * - rating-count-leader: highest userRatingCount among available rows.
 * - rating-count-low: lowest userRatingCount among available rows (only when
 *   more than one available row carries a rating count, and it differs from the
 *   leader so we never tag a single value as both).
 * - rating-leader / rating-low: same idea for averageUserRating.
 * - emerging: available and below the median rating count, AND charting in the
 *   top 100 (per docs/PLAN.md: chart presence + below-median traction signals a
 *   market that is gaining visibility faster than its rating base reflects).
 *   When no chart data is present (chart enrichment disabled), this falls back
 *   to the M2 behaviour of below-median rating count alone so the signal still
 *   works on a static deploy.
 * - low-visibility: available but either has no rating count at all, OR is
 *   below the median rating count AND not charting in the top 100 (only when
 *   chart data is present).
 * - price-mismatch: available price differs from the most common price among
 *   available rows (the modal price bucket).
 * - unavailable: row is unavailable or errored.
 *
 * A row only ever carries one of {rating-count-leader, rating-count-low} and
 * one of {rating-leader, rating-low}; ties resolve in favor of the leader tag.
 *
 * @param {RegionRow[]} rows
 * @returns {ContrastResult}
 */
export function computeContrast(rows) {
  const safeRows = Array.isArray(rows) ? rows : [];
  const byRegion = new Map();

  // Seed every row with an empty contrast entry so the UI can look up any code.
  for (const row of safeRows) {
    byRegion.set(row.countryCode, {
      countryCode: row.countryCode,
      available: Boolean(row.available) && !row.error,
      ratingCountShare: 0,
      ratingDeviation: null,
      localizationGap: null,
      tags: [],
    });
  }

  const availableRows = safeRows.filter((r) => r.available && !r.error);
  const ratingCounts = availableRows
    .map((r) => r.userRatingCount)
    .filter((n) => typeof n === 'number' && Number.isFinite(n));
  const maxRatingCount = ratingCounts.length ? Math.max(...ratingCounts) : 0;
  const medianRatingCount = median(ratingCounts);

  // Compute mean rating across available rows for deviation highlighting.
  const ratings = availableRows
    .map((r) => r.averageUserRating)
    .filter((n) => typeof n === 'number' && Number.isFinite(n));
  const meanRating = ratings.length >= 2
    ? ratings.reduce((s, v) => s + v, 0) / ratings.length
    : null;

  const ratingCountLeaderCode = leaderCode(availableRows, 'userRatingCount', 'max');
  const ratingCountLowCode = leaderCode(availableRows, 'userRatingCount', 'min');
  const ratingLeaderCode = leaderCode(availableRows, 'averageUserRating', 'max');
  const ratingLowCode = leaderCode(availableRows, 'averageUserRating', 'min');
  const modalPrice = modePrice(availableRows);

  for (const row of safeRows) {
    const entry = byRegion.get(row.countryCode);
    if (!entry) continue;

    if (!entry.available) {
      entry.tags.push(TAGS.UNAVAILABLE);
      continue;
    }

    const count = numericOrNull(row.userRatingCount);
    if (maxRatingCount > 0 && count !== null) {
      entry.ratingCountShare = count / maxRatingCount;
    }

    // Rating count leader/low. Only tag "low" when it differs from the leader.
    if (row.countryCode === ratingCountLeaderCode) {
      entry.tags.push(TAGS.RATING_COUNT_LEADER);
    } else if (
      row.countryCode === ratingCountLowCode &&
      ratingCountLowCode !== ratingCountLeaderCode
    ) {
      entry.tags.push(TAGS.RATING_COUNT_LOW);
    }

    // Average rating leader/low.
    if (row.countryCode === ratingLeaderCode) {
      entry.tags.push(TAGS.RATING_LEADER);
    } else if (
      row.countryCode === ratingLowCode &&
      ratingLowCode !== ratingLeaderCode
    ) {
      entry.tags.push(TAGS.RATING_LOW);
    }

    // Rating deviation from global mean (≥0.5 star threshold).
    const rowRating = numericOrNull(row.averageUserRating);
    if (meanRating !== null && rowRating !== null) {
      const dev = Math.round((rowRating - meanRating) * 100) / 100;
      entry.ratingDeviation = dev;
      if (dev >= 0.5) {
        entry.tags.push(TAGS.RATING_ABOVE_AVG);
      } else if (dev <= -0.5) {
        entry.tags.push(TAGS.RATING_BELOW_AVG);
      }
    }

    // Visibility signals. Chart rank, when present, splits "below median"
    // rows into emerging (still visible on a chart) vs low-visibility (not).
    const charting = isCharting(row);
    const hasChartData = chartDataPresent(row);
    if (count === null) {
      entry.tags.push(TAGS.LOW_VISIBILITY);
    } else if (medianRatingCount !== null && count < medianRatingCount) {
      if (hasChartData) {
        entry.tags.push(charting ? TAGS.EMERGING : TAGS.LOW_VISIBILITY);
      } else {
        // No chart enrichment available -> M2 fallback.
        entry.tags.push(TAGS.EMERGING);
      }
    }

    // Price outlier vs the modal price among available rows.
    const price = numericOrNull(row.price);
    if (modalPrice !== null && price !== null && price !== modalPrice) {
      entry.tags.push(TAGS.PRICE_MISMATCH);
    }

    const localizationGap = detectLocalizationGap(row);
    if (localizationGap) {
      entry.localizationGap = localizationGap;
      entry.tags.push(TAGS.LOCALIZATION_GAP);
    }
  }

  const mostTractionCode = ratingCountLeaderCode;
  const weakestCode =
    ratingCountLowCode && ratingCountLowCode !== ratingCountLeaderCode
      ? ratingCountLowCode
      : null;

  return {
    byRegion,
    maxRatingCount,
    mostTractionCode,
    weakestCode,
    insight: buildInsight(safeRows, mostTractionCode, weakestCode),
  };
}

/**
 * Build the one-line insight string from the leader/laggard codes.
 * @param {RegionRow[]} rows
 * @param {string|null} mostCode
 * @param {string|null} weakCode
 * @returns {string}
 */
function buildInsight(rows, mostCode, weakCode) {
  const label = (code) => {
    const row = rows.find((r) => r.countryCode === code);
    if (!row) return code;
    return `${row.countryName} (${String(row.countryCode).toUpperCase()})`;
  };

  const parts = [];
  if (mostCode) parts.push(`Most traction: ${label(mostCode)}`);
  if (weakCode) parts.push(`Weakest: ${label(weakCode)}`);
  if (parts.length === 0) {
    return 'No rating data available to compare across regions.';
  }
  return `${parts.join(' · ')}. Rating count is a popularity proxy, not downloads.`;
}

/**
 * Find the countryCode of the row with the max or min finite value for a field
 * among the given rows. Ties resolve to the first row in input order, which
 * keeps the result stable for a given storefront ordering.
 * @param {RegionRow[]} rows
 * @param {keyof RegionRow} field
 * @param {'max'|'min'} mode
 * @returns {string|null}
 */
function leaderCode(rows, field, mode) {
  let best = null;
  let bestVal = null;
  for (const row of rows) {
    const val = numericOrNull(row[field]);
    if (val === null) continue;
    if (
      bestVal === null ||
      (mode === 'max' && val > bestVal) ||
      (mode === 'min' && val < bestVal)
    ) {
      best = row.countryCode;
      bestVal = val;
    }
  }
  return best;
}

/**
 * Most common finite price among rows. Ties resolve to the lowest price, since
 * the cheapest tier (often Free) is the most natural "baseline" to flag
 * deviations from. Returns null when no row has a finite price.
 * @param {RegionRow[]} rows
 * @returns {number|null}
 */
function modePrice(rows) {
  const counts = new Map();
  for (const row of rows) {
    const price = numericOrNull(row.price);
    if (price === null) continue;
    counts.set(price, (counts.get(price) ?? 0) + 1);
  }
  if (counts.size === 0) return null;

  let mode = null;
  let modeCount = -1;
  for (const [price, n] of counts) {
    if (n > modeCount || (n === modeCount && price < mode)) {
      mode = price;
      modeCount = n;
    }
  }
  return mode;
}

/**
 * Median of a list of numbers. Returns null for an empty list.
 * @param {number[]} values
 * @returns {number|null}
 */
function median(values) {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid];
}

function numericOrNull(value) {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * Whether a row carries any usable chart-rank enrichment. A row is only
 * considered to "have chart data" when at least one rank field is a finite
 * number or the row explicitly read a chart and is not charting. Rows from a
 * chart-disabled build leave these fields undefined and are treated as having
 * no chart data, so the emerging/low-visibility split degrades to M2 behaviour.
 * @param {RegionRow & {chartRankFree?: number|null, chartRankPaid?: number|null, chartUnavailable?: boolean, chartLabel?: string|null}} row
 * @returns {boolean}
 */
function chartDataPresent(row) {
  if (numericOrNull(row.chartRankFree) !== null) return true;
  if (numericOrNull(row.chartRankPaid) !== null) return true;
  // A successful "not charting" read also counts as chart data being present.
  if (row.chartUnavailable === false && typeof row.chartLabel === 'string') {
    return true;
  }
  return false;
}

/**
 * Whether a row is currently charting in either the free or paid feed.
 * @param {{chartRankFree?: number|null, chartRankPaid?: number|null}} row
 * @returns {boolean}
 */
function isCharting(row) {
  return (
    numericOrNull(row.chartRankFree) !== null ||
    numericOrNull(row.chartRankPaid) !== null
  );
}
